import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const lastActivity = useRef(Date.now());
  const lastRefresh = useRef(Date.now());

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  const logout = useCallback(async (reason) => {
    try { await api.post("/auth/logout"); } catch { /* cookies already gone */ }
    if (reason === "idle") sessionStorage.setItem("signed_out_reason", "idle");
    setUser(false);
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  useEffect(() => {
    const onSignedOut = () => setUser(false);
    window.addEventListener("auth:signed-out", onSignedOut);
    return () => window.removeEventListener("auth:signed-out", onSignedOut);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const mark = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    lastActivity.current = Date.now();
    lastRefresh.current = Date.now();
    const timer = setInterval(async () => {
      const idleMs = Date.now() - lastActivity.current;
      const limitMs = (user.idle_timeout_minutes || 30) * 60_000;
      if (idleMs >= limitMs) { await logout("idle"); return; }
      if (Date.now() - lastRefresh.current > 4 * 60_000 && idleMs < 4 * 60_000) {
        try { await api.post("/auth/refresh"); lastRefresh.current = Date.now(); } catch { setUser(false); }
      }
    }, 20_000);
    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, mark));
    };
  }, [user?.id, user?.idle_timeout_minutes, logout, user]);

  const value = useMemo(() => ({ user, setUser, refreshUser, logout }), [user, refreshUser, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
